import * as React from "react";
import { Box, Stack, Typography} from "@mui/material";
import AccountSearchList from "./accountSearchList";
import CESystemUserList from "./cESystemUserList";
import { AccExecClientResponse, CeSystemUserResponse, SalesChangeRequest } from "../../../../services/types/clientTypes";
import FlexGrid from "../../../../layouts/flexGrid";
import AccountManagerSubDataGridPro from "./accountManagerSubDataGridPro";
import CESystemUserSubDataGridPro from "./cESystemUserSubDataGridPro";
import CclButton from "../../../../components/common/cclButtons/cclButton";
import CclAlertSnackbar from "../../../../components/common/cclAlertSnackbar";
import {
    useSaveSalesChangeMutation,
    useValidateDateOverlapMutation,
} from "../../../../services/cclTokenedCustomerHierarchyApi";
import CclGenericAccordian from "../../../../components/common/cclGenericAccordian";
import CclGenericConfirmationDialog from "../../../../components/common/cclGenericConfirmationDialog";

export type AccountManagerPanelParams = {};

export const AccountManagerPanel: React.FC = (prop) => {
        // redux state
    const [searchAccountOrCETab, setsearchAccountOrCETab] = React.useState<boolean>(true);
    const [selectAccountExecData, setSelectAccountExecData] = React.useState<AccExecClientResponse[]|undefined>();
    const [selectCESystemUserData, setSelectCESystemUserData] = React.useState<CeSystemUserResponse[]|undefined>();
    const [confirmSave, setConfirmSave] = React.useState<boolean>(false);
    const [refreshTrigger, setRefreshTrigger] = React.useState<number>(0);
    const [createMappingsExpanded, setCreateMappingsExpanded] = React.useState<boolean>(false);
    const [showOverlapConfirm, setShowOverlapConfirm] = React.useState<boolean>(false);
    const pendingSaveDataRef = React.useRef<SalesChangeRequest | null>(null);

    const [handleSaveMapping, {isError}] = useSaveSalesChangeMutation();
    const [validateDateOverlap] = useValidateDateOverlapMutation();

    const onCancel = () => {
        setSelectAccountExecData([]);
        setSelectCESystemUserData([]);
        setCreateMappingsExpanded(false);
    };

    const performSave = (data: SalesChangeRequest) => {
        handleSaveMapping(data)
            .unwrap()
            .then(() => {
                setConfirmSave(true);
                setRefreshTrigger((n) => n + 1);
                setsearchAccountOrCETab(true);
                setSelectAccountExecData([]);
                setSelectCESystemUserData([]);
                setCreateMappingsExpanded(false);
            })
    };

    const handleSave = () => {
        if(selectAccountExecData && selectCESystemUserData){

            const data: SalesChangeRequest = {
                mapperId: selectAccountExecData[0].mapperID,
                fiscal_Year: selectAccountExecData[0].fiscalYear,
                parent_Key: selectAccountExecData[0].parentKey,
                systemID: selectCESystemUserData[0].systemUserId,
                effectiveStartDate: selectAccountExecData[0].effectiveStartDate,
                effectiveEndDate: selectAccountExecData[0].effectiveEndDate,
                account_name: `${selectCESystemUserData[0].firstName} ${selectCESystemUserData[0].lastName}`,
                secondary_Ind: selectAccountExecData[0].newClientIndicator,
            }

            validateDateOverlap({
                parentKey: selectAccountExecData[0].parentKey,
                esDate: selectAccountExecData[0].effectiveStartDate,
                eeDate: selectAccountExecData[0].effectiveEndDate,
                fiscal: selectAccountExecData[0].fiscalYear,
            })
                .unwrap()
                .then((overlapCount) => {
                    if (overlapCount > 0) {
                        pendingSaveDataRef.current = data;
                        setShowOverlapConfirm(true);
                    } else {
                        performSave(data);
                    }
                });
        }
    }

    const handleOverlapConfirmOk = () => {
        setShowOverlapConfirm(false);
        if (pendingSaveDataRef.current) {
            performSave(pendingSaveDataRef.current);
            pendingSaveDataRef.current = null;
        }
    };

    const handleOverlapConfirmCancel = () => {
        setShowOverlapConfirm(false);
        pendingSaveDataRef.current = null;
    };

    return (
            <Stack height={1} width={1}>
                <CclGenericConfirmationDialog
                    open={showOverlapConfirm}
                    title="Warning"
                    msg={"Dates overlap an existing mapping! \n Are you sure want to continue?"}
                    onOk={handleOverlapConfirmOk}
                    onCancel={handleOverlapConfirmCancel}
                />
                <Stack  direction="row" justifyContent="center"  alignItems="center"  spacing={2}>
                    <CclButton 
                        onClick={(evt) => {
                            evt.stopPropagation(); setsearchAccountOrCETab(true);
                        } }
                        sx={{ m: 1, mr: 2, border: 1, borderRadius: 1, padding: "5px", }} 
                        mode={searchAccountOrCETab ? "primary" : "secondary"}
                    >
                         Account Executive Client Search
                    </CclButton>
                    <CclButton 
                        onClick={(evt) => {
                            evt.stopPropagation(); setsearchAccountOrCETab(false);
                        } }
                        sx={{ m: 1, mr: 2, border: 1, borderRadius: 1, padding: "5px", }} 
                        mode={searchAccountOrCETab ? "secondary" : "primary"}
                    >
                            Account Owner Search
                    </CclButton>
                </Stack>  
                <CclGenericAccordian
                    sx={{mt: 2}}
                    headingText={"Create Mappings"}
                    expanded={createMappingsExpanded}
                    onExpandedChange={setCreateMappingsExpanded}
                >
                    <Box
                        sx={{
                            width: 1,
                            m: 2,
                            p: 3,
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <CclAlertSnackbar 
                            open={confirmSave}
                            message={isError? "User mapping failed" : "User mapped to client"} 
                            severity={isError? "error" : "success"} 
                            onClose={() => setConfirmSave(false)}                        
                        />

                        <Typography variant="caption" display="block" gutterBottom>The following User.....
                        </Typography> 

                        <FlexGrid>
                            <CESystemUserSubDataGridPro
                                accountCELists={selectCESystemUserData??[]}
                                isLoading={false}/>
                            </FlexGrid>
                            <Typography variant="caption" display="block" gutterBottom>will now have the following Client
                            </Typography>                    
                        <FlexGrid>
                            <AccountManagerSubDataGridPro
                                accountCELists={selectAccountExecData ?? []}
                                isLoading={false}/>
                        </FlexGrid>

                        <Stack direction={"row"}>
                            <CclButton 
                                onClick={()=> onCancel()}
                                sx={{
                                    width: 0.1,
                                    m: 2,
                                    mt: 4,
                                    ml: "auto",
                                }}
                                mode={"primary"}> 
                                Cancel
                            </CclButton>
                            <CclButton 
                                onClick={(evt) => {
                                    evt.stopPropagation(); handleSave();
                                }}
                                sx={{
                                    width: 0.1,
                                    m: 2,
                                    mt: 4,
                                }}
                                disabled={!(selectAccountExecData && selectCESystemUserData)} 
                                mode={"primary"}                                        
                            >
                                Save Mapping(s)
                            </CclButton>
                        </Stack>
                    </Box>
                </CclGenericAccordian>
                <CclGenericAccordian headingText="Search Clients" sx={{mt: 4}}>
                    <Box sx={{ display: searchAccountOrCETab ? "block" : "none" }}>
                        <AccountSearchList
                            OnSelectedAccountExecData={setSelectAccountExecData}
                            refreshTrigger={refreshTrigger}
                        />
                    </Box>
                    <Box sx={{ display: searchAccountOrCETab ? "none" : "block" }}>
                        <CESystemUserList OnSelectedCESystemUserData={setSelectCESystemUserData}/>
                    </Box>
                 </CclGenericAccordian>
            </Stack> 
   );
                };

export default AccountManagerPanel;
